import { useEffect, useRef } from 'react';
import { eventNameFor, writeLocalStorage } from './useLocalStorage';
import { getMyAchievements, putMyAchievements, AchievementRecord } from '../api/client';
import { PlayerAchievement } from '../types/achievements';

// Must match the keys used by useAchievements / AchievementsPanel.
const ACH_KEY = 'histocracy_achievements';
const PTS_KEY = 'histocracy_total_points';

// Streak achievements (streak_3, _5, _10…) unlock seconds apart during a game.
// A longer debounce collapses that burst into a single push, cutting Upstash
// command usage several-fold for the cost of a slightly later server write.
const PUSH_DEBOUNCE_MS = 2500;

function readLocal(): AchievementRecord {
  let achievements: PlayerAchievement[] = [];
  let totalPoints = 0;
  try {
    achievements = JSON.parse(window.localStorage.getItem(ACH_KEY) || '[]');
    if (!Array.isArray(achievements)) achievements = [];
  } catch {
    achievements = [];
  }
  try {
    const raw = JSON.parse(window.localStorage.getItem(PTS_KEY) || '0');
    totalPoints = Number.isFinite(raw) ? raw : 0;
  } catch {
    totalPoints = 0;
  }
  return { achievements, totalPoints };
}

// A device with no unlocked achievements and no points has nothing to merge
// upward — we can pull (GET) instead of merge-pushing (GET+SET), halving the
// command cost of first sign-in on a fresh device.
function isEmpty(record: AchievementRecord): boolean {
  return record.achievements.length === 0 && record.totalPoints === 0;
}

function sameRecord(a: AchievementRecord, b: AchievementRecord): boolean {
  if (a.totalPoints !== b.totalPoints) return false;
  if (a.achievements.length !== b.achievements.length) return false;
  const ids = new Set(a.achievements.map((x) => x.id));
  return b.achievements.every((x) => ids.has(x.id));
}

// Reconcile the local achievement cache with the server. Mounted once at the
// app root. When the wallet is authenticated, it merges local + server (the
// server is the merge authority) so achievements carry over across devices and
// survive a cleared cache, then pushes subsequent unlocks as they happen.
export function useAchievementSync(authedAddress: string | null): void {
  // Set while we apply a server result locally, so the resulting storage
  // broadcast doesn't bounce straight back as another push (feedback loop).
  const applyingRemote = useRef(false);
  const syncedFor = useRef<string | null>(null);

  function applyRemote(record: AchievementRecord) {
    const local = readLocal();
    if (sameRecord(local, record)) return;
    applyingRemote.current = true;
    try {
      writeLocalStorage(ACH_KEY, record.achievements);
      writeLocalStorage(PTS_KEY, record.totalPoints);
    } finally {
      applyingRemote.current = false;
    }
  }

  // Initial pull + merge whenever we (re)authenticate as a new wallet.
  useEffect(() => {
    if (!authedAddress) {
      syncedFor.current = null;
      return;
    }
    if (syncedFor.current === authedAddress) return;

    let cancelled = false;
    (async () => {
      try {
        const local = readLocal();
        // Fresh device with no local progress: a one-way pull (GET) is enough
        // and avoids a pointless empty merge-write. Otherwise push the local
        // copy so the server merges it and returns the canonical record.
        const record = isEmpty(local)
          ? await getMyAchievements()
          : await putMyAchievements(local);
        if (cancelled) return;
        applyRemote(record);
        syncedFor.current = authedAddress;
      } catch {
        // Offline or store unavailable — keep working from localStorage and
        // retry on the next unlock-driven push.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authedAddress]);

  // Push local changes (new unlocks, point gains) to the server, debounced.
  useEffect(() => {
    if (!authedAddress) return;

    let timer: ReturnType<typeof setTimeout> | undefined;

    const push = () => {
      putMyAchievements(readLocal())
        .then((merged) => applyRemote(merged))
        .catch(() => {
          /* keep local; will retry on next change */
        });
    };

    const onChange = () => {
      if (applyingRemote.current) return; // ignore our own writes
      if (timer) clearTimeout(timer);
      timer = setTimeout(push, PUSH_DEBOUNCE_MS);
    };

    window.addEventListener(eventNameFor(ACH_KEY), onChange);
    window.addEventListener(eventNameFor(PTS_KEY), onChange);
    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener(eventNameFor(ACH_KEY), onChange);
      window.removeEventListener(eventNameFor(PTS_KEY), onChange);
    };
  }, [authedAddress]);
}
